/**
 * Sentry initialization for the React/Vite frontend.
 *
 * No-op when VITE_SENTRY_DSN is unset — safe to import unconditionally.
 *
 * Required env vars (all optional, set in Vercel project settings):
 *   VITE_SENTRY_DSN          - project DSN from sentry.io
 *   VITE_SENTRY_ENVIRONMENT  - 'production' | 'staging' | 'development' (default: import.meta.env.MODE)
 *   VITE_SENTRY_RELEASE      - git sha or version tag
 *   VITE_SENTRY_TRACES_SAMPLE_RATE  - float 0..1 (default: 0.1 in prod, 1.0 otherwise)
 */

import * as Sentry from '@sentry/react';

let initialized = false;

export function initSentryWeb(): void {
  if (initialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
  const release = import.meta.env.VITE_SENTRY_RELEASE || undefined;
  const tracesSampleRate = parseFloat(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ||
      (environment === 'production' ? '0.1' : '1.0'),
  );

  Sentry.init({
    dsn,
    environment,
    release,
    tracesSampleRate,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    sendDefaultPii: false,
    // ResizeObserver loop and benign network noise → drop.
    ignoreErrors: [
      /ResizeObserver loop/,
      /Network request failed/,
      /Load failed/,
    ],
  });

  initialized = true;
}

export { Sentry };
